import clsx from "clsx"
import { Ref, cloneElement, forwardRef } from "react"

import useDebouncedCallback from "@hooks/useDebouncedCallback"

import { Skeleton, SkeletonTypography } from "@particles/index"

import { Icon } from "@atoms"
import { AvatarProps } from "@atoms/Avatar/Avatar.types"

import styles from "./Chip.module.scss"
import { ChipProps } from "./Chip.types"

function Chip(props: ChipProps, ref: Ref<HTMLButtonElement>) {
    const {
        children,

        variant = "default",
        size,
        type = "button",
        className,

        icon,
        iconClassName,
        avatar,

        isTabbable = true,

        onClick,
        noDebounce = false,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,

        // No default: false still marks a toggle (one that's off), so only chips that toggle pass it.
        isSelected,
        isError = false,
        isDisabled = false,
        isLoading = false,

        ...rest
    } = props

    const debouncedClick = useDebouncedCallback(onClick)

    const iconSize = 14
    const avatarSize: AvatarProps["size"] = size === "sm" ? "xxs" : "xs"

    return (
        <button
            ref={ref}
            className={clsx(
                styles.base,
                className,
                {
                    [styles.isSelected]: isSelected,
                    [styles.hasError]: isError,
                    [styles.isLoading]: isLoading,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                },
                styles[`${size}Size`],
            )}
            type={type}
            onClick={noDebounce ? onClick : debouncedClick}
            disabled={isDisabled || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isLoading}
            aria-pressed={isSelected}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            {...rest}
        >
            {variant === "icon" && icon && (
                <span className={styles.icon}>
                    {isLoading ? (
                        <Skeleton circle={true} height={iconSize} width={iconSize} enableAnimation={true} />
                    ) : (
                        <Icon icon={icon} className={iconClassName} size={iconSize} container="fixedHeight" />
                    )}
                </span>
            )}
            {variant === "avatar" && avatar && (
                <span className={styles.avatar}>{cloneElement(avatar, { size: avatarSize, isLoading })}</span>
            )}
            {isLoading ? (
                <span className={styles.labelSkeleton}>
                    <SkeletonTypography variant={size === "sm" ? "captionMD" : "bodyCompact"} />
                </span>
            ) : (
                <span className={styles.label}>{children}</span>
            )}
        </button>
    )
}

export default forwardRef(Chip)
